// @ts-nocheck
import React from 'react';
import raf from 'raf';

interface Props {
  open: boolean;
  children: React.ReactNode;
  fixedHeight?: number;
  fixedWidth?: number;
  dimension?: string;
  duration?: number;
  className?: string;
  dataTestID?: string;
}

export default class Collapse extends React.Component<Props, {}> {
  static defaultProps = {
    dimension: 'height',
    duration: 300,
  };

  node: HTMLDivElement;

  wrapper: HTMLDivElement;

  _animating: NodeJS.Timer;

  componentDidMount() {
    this.toggle(this.props.open, false);
  }

  UNSAFE_componentWillReceiveProps({ open }) {
    const changed = open !== this.props.open;

    if (changed) {
      this.toggle(open);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._animating);
  }

  render() {
    // use inline-block for width-based blocks
    const styles = this.isHeight ? null : { display: 'inline-block' };

    const { dataTestID } = this.props;

    return (
      <div
        ref={this.bindContainer}
        style={styles}
        className={this.props.className}
        data-testid={dataTestID}
      >
        <div
          ref={this.bindWrapper}
          style={styles}
        >
          {this.props.children}
        </div>
      </div>
    );
  }

  bindContainer = (node) => {
    this.node = node;
  };

  bindWrapper = (node) => {
    this.wrapper = node;
  };

  get totalHeight() {
    return this.wrapper ? this.wrapper.clientHeight : 0;
  }

  get totalWidth() {
    return this.wrapper ? this.wrapper.clientWidth : 0;
  }

  get isHeight() {
    return this.props.dimension === 'height';
  }

  get isAnimating() {
    return this._animating;
  }

  get fullDimension() {
    return this.isHeight ? this.props.fixedHeight || this.totalHeight
      : this.props.fixedWidth || this.totalWidth;
  }

  get clientDimension() {
    return this.isHeight ? this.node.clientHeight
      : this.node.clientWidth;
  }

  get currentDimension() {
    return this.isAnimating ? this.clientDimension
      : this.fullDimension;
  }

  get animatedTransition() {
    if (this.isHeight) {
      return `height ${this.props.duration}ms ease`;
    }

    return `min-width ${this.props.duration}ms ease, width ${this.props.duration}ms ease`;
  }

  set animatedDimension(value) {
    if (this.node) {
      if (this.isHeight) {
        this.node.style.height = value;
      } else {
        this.node.style.width = value;
        this.node.style.minWidth = value;
      }
    }
  }

  toggle(open, animate = true) {
    const transition = animate ? this.animatedTransition : '';
    const tick = animate ? raf : (next) => next();

    this.node.style.overflow = 'hidden';

    if (open) {
      // When opening the box, the overflow and height/width are already 0, so we set
      // the transition and the final dimension.
      this.node.style.transition = transition;
      this.animatedDimension = `${this.fullDimension.toString()}px`;
    } else {
      // Closing the box is the meat of the hard part of animating dynamic content.
      // It seems to work when combined with requestAnimationFrame to allow the layout
      // to reset. When this first gets called, the height/width is set to `auto`, so
      // we need to first set it to the current clientHeight/clientWidth so it can be
      // animated. Then we animated it to 0.
      tick(() => {
        this.animatedDimension = `${this.currentDimension.toString()}px`;

        // delay another frame so the height is now set
        tick(() => {
          if (this.node) {
            this.node.style.transition = transition;
          }
          this.animatedDimension = '0';
        });
      });
    }

    clearTimeout(this._animating);

    this._animating = setTimeout(() => {
      this._animating = null;

      // when ending, remove the transition property
      this.node.style.transition = '';

      if (open) {
        // after opening, clear the height/width and overflow
        this.animatedDimension = '';
        this.node.style.overflow = '';
      } else {
        // after closing, set the height/width to 0 and overflow hidden
        this.animatedDimension = '0';
        this.node.style.overflow = 'hidden';
      }
    }, animate ? this.props.duration : 0);
  }
}
